import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AUTH_ENABLED } from './authConfig';
import { API_BASE_URL } from '../api/baseUrl';
import { fixMojibakeUtf8 } from '../utils/text';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const bypassUser = useMemo(() => ({
    user_id: 'bypass',
    name: '免登录模式',
  }), []);

  const [user, setUser] = useState(AUTH_ENABLED ? null : bypassUser);
  const [loading, setLoading] = useState(AUTH_ENABLED);

  const normalizeUser = useCallback((rawUser) => {
    if (!rawUser) return rawUser;
    const next = { ...(rawUser || {}) };
    if (next.nickname) next.nickname = fixMojibakeUtf8(next.nickname);
    if (next.name) next.name = fixMojibakeUtf8(next.name);
    if (next.wechat && typeof next.wechat === 'object') {
      next.wechat = { ...next.wechat };
      if (next.wechat.nickname) next.wechat.nickname = fixMojibakeUtf8(next.wechat.nickname);
    }
    return next;
  }, []);

  const refreshMe = useCallback(async ({ silent = false } = {}) => {
    if (!AUTH_ENABLED) {
      setUser(bypassUser);
      return bypassUser;
    }

    if (!silent) setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        credentials: 'include',
      });

      if (resp.status === 401) {
        setUser(null);
        return null;
      }

      if (!resp.ok) {
        setUser(null);
        return null;
      }

      const data = await resp.json();
      if (data?.status === 'success') {
        const nextUser = normalizeUser(data.user ?? null);
        setUser(nextUser);
        return nextUser;
      }

      setUser(null);
      return null;
    } catch (_) {
      setUser(null);
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [bypassUser, normalizeUser]);

  const logout = useCallback(async () => {
    if (!AUTH_ENABLED) {
      setUser(bypassUser);
      return;
    }

    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
    } catch (_) {
      // ignore
    } finally {
      setUser(null);
    }
  }, [bypassUser]);

  useEffect(() => {
    if (!AUTH_ENABLED) return;
    refreshMe().catch(() => {});
  }, [refreshMe]);

  const value = useMemo(() => ({
    user,
    loading,
    refreshMe,
    logout,
  }), [loading, logout, refreshMe, user]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
